/*
  Carriers score some patterns as spam and can drop a text without reporting a failure.
  These rewrites keep the sender's meaning while avoiding the two that bite here: a
  currency symbol, and a date written with slashes.

  The rewrite is offered rather than applied silently - the compose window puts the
  adjusted text back in the body, shows what it changed, and leaves the sender to send
  again - so nobody discovers their message was edited only after it arrived.
*/

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// "$12,000" / "$1000" / "$8.25". The amount keeps whatever grouping and decimals were
// typed; only the symbol moves and becomes a word.
const MONEY_PATTERN = /\$\s*(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?)/g;

// "5/6", "6/7/28", "10/16/2024". The leading group is a captured character rather than a
// lookbehind, which not every browser supports; both it and the trailing check keep the
// match off longer digit/slash runs that are not dates.
const DATE_PATTERN = /(^|[^\d/])(\d{1,2})\/(\d{1,2})(?:\/(\d{4}|\d{2}))?(?![\d/])/g;

export interface SmsTextAdjustmentType {
  from: string;
  to: string;
}

export interface SmsSpamSafeResultType {
  text: string;
  adjustments: SmsTextAdjustmentType[];
}

/*
  Anything that is not a real month/day is left exactly as written - better to send an
  unchanged "13/45" than to invent a date out of something that was never one.
*/
const spellOutDate = (month: number, day: number, yearText?: string) => {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const monthName = MONTHS[month - 1];

  if (!yearText) return `${monthName} ${day}`;

  // A two-digit year is read as this century, so "28" is the 2028 the sender meant.
  const year = yearText.length === 2 ? 2000 + Number(yearText) : Number(yearText);

  return `${monthName} ${day} ${year}`;
};

export function makeSmsSpamSafe(text: string): SmsSpamSafeResultType {
  if (!text) return { text, adjustments: [] };

  const adjustments: SmsTextAdjustmentType[] = [];

  // Same from/to more than once is one adjustment as far as the sender is concerned.
  const record = (from: string, to: string) => {
    if (!adjustments.some((a) => a.from === from && a.to === to)) adjustments.push({ from, to });
  };

  const withoutCurrency = text.replace(MONEY_PATTERN, (match, amount) => {
    const replacement = `${amount} dollars`;
    record(match, replacement);
    return replacement;
  });

  const withSpelledDates = withoutCurrency.replace(
    DATE_PATTERN,
    (match, prefix: string, month: string, day: string, year?: string) => {
      const spelled = spellOutDate(Number(month), Number(day), year);

      if (!spelled) return match;

      // The captured leading character is not part of the date, so it is put back.
      const original = match.slice(prefix.length);
      record(original, spelled);

      return `${prefix}${spelled}`;
    }
  );

  return { text: withSpelledDates, adjustments };
}

export interface SmsSpamSafeBodyType {
  /** How this body is named to the sender - a batch groups its notice by it. */
  label: string;
  text: string;
}

export interface SmsSpamSafeBodiesResultType {
  /** Every body, rewritten, in the order given - unchanged ones included. */
  bodies: SmsSpamSafeBodyType[];
  /** Only the bodies that actually changed, so a caller can list just those. */
  changed: { label: string; adjustments: SmsTextAdjustmentType[] }[];
}

/*
  The same rewrite over a set of bodies at once.

  A single send has one body in one visible field, so it can rewrite it, stop, and let the sender
  look at what changed before pressing Send again. A batch has the default text body plus one per
  customized row, and all but one of them are off screen - so it asks about them together instead,
  and the caller shows `changed` before committing.

  Kept beside makeSmsSpamSafe rather than in either dialog so the two can never disagree about what
  counts as a rewrite.
*/
export function makeBodiesSmsSpamSafe(bodies: SmsSpamSafeBodyType[]): SmsSpamSafeBodiesResultType {
  const results = bodies.map((body) => ({ label: body.label, ...makeSmsSpamSafe(body.text) }));

  return {
    bodies: results.map(({ label, text }) => ({ label, text })),
    changed: results
      .filter((result) => result.adjustments.length > 0)
      .map(({ label, adjustments }) => ({ label, adjustments })),
  };
}
